import { useTranslation } from 'react-i18next'
import { Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'

import colors from '@/assets/scss/_themes-vars.module.scss'
import { SyncQueueItem } from '@/core/offline/types/database.types'

import { formatDate, getQueueItemEntityIcon, getQueueItemStatusColor, getQueueItemActionLabel } from './helpers'

export function SyncQueueTable({ queueItems }: { queueItems: SyncQueueItem[] }) {
  const { t } = useTranslation()

  return (
    <Paper>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6">
          {t('sync.syncQueue')} ({queueItems.length} {t('sync.items')})
        </Typography>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('offline.sync.dashboard.table.type').toUpperCase()}</TableCell>
              <TableCell>{t('offline.sync.dashboard.table.action').toUpperCase()}</TableCell>
              <TableCell>{t('offline.sync.dashboard.table.entity').toUpperCase()}</TableCell>
              <TableCell align="center">{t('offline.sync.dashboard.table.status').toUpperCase()}</TableCell>
              <TableCell align="center">{t('offline.sync.dashboard.table.priority').toUpperCase()}</TableCell>
              <TableCell>{t('offline.sync.dashboard.table.created').toUpperCase()}</TableCell>
              <TableCell>{t('offline.sync.dashboard.table.lastAttempt').toUpperCase()}</TableCell>
              <TableCell align="center">{t('offline.sync.dashboard.table.attempts').toUpperCase()}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {queueItems.length === 0 ? (
              <EmptyRow />
            ) : (
              queueItems.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <span>{getQueueItemEntityIcon(item.entityType)}</span>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.entityType}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getQueueItemActionLabel(item.action || item.operation, t)}
                      size="small"
                      variant="outlined"
                      color={(item.action || item.operation) === 'create' ? 'success' : (item.action || item.operation) === 'update' ? 'info' : 'error'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: colors.gray600 }}>
                      {item.entityId}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={item.status} size="small" color={getQueueItemStatusColor(item.status)} variant="filled" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={item.priority}
                      size="small"
                      variant="outlined"
                      color={item.priority >= 2 ? 'error' : item.priority === 1 ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '13px' }}>
                      {formatDate(typeof item.createdAt === 'string' ? new Date(item.createdAt).getTime() : item.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '13px' }}>
                      {item.lastAttempt ? formatDate(typeof item.lastAttempt === 'string' ? new Date(item.lastAttempt).getTime() : item.lastAttempt) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {item.attempts} / {item.maxAttempts}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}

function EmptyRow() {
  const { t } = useTranslation()
  return (
    <TableRow>
      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          {t('sync.noItemsInQueue')}
        </Typography>
      </TableCell>
    </TableRow>
  )
}
